<script setup lang="ts">
import {
  type ComponentInternalInstance,
  computed,
  type CSSProperties,
  getCurrentInstance,
  inject,
  nextTick,
  onMounted,
  provide,
  ref,
  toRaw,
  useAttrs,
  useSlots,
  useTemplateRef,
  watch,
} from "vue";
import type { ComponentPosition } from "../../models/types";
import { MenuEntry } from "../../models/menu-entry";
import type { BcMenuItemInstance } from "../../models/instance-types";
import BcContextMenu from "./bc-context-menu.vue";
import BcButton from "../../components/bc-button.vue";
import BcCollapseTransition from "../../components/bc-collapse-transition.vue";

export type BcMenuItemProps = {
  active?: boolean;
  header?: boolean;
  componentIndent?: boolean;
  componentBackground?: boolean;
  indent?: boolean;
  selected?: boolean;
  beginGroup?: boolean;
  borderAtTop?: boolean;
  borderAtBottom?: boolean;
  componentMaxHeight?: string;
  componentPosition?: ComponentPosition;
  contextMenuActions?: MenuEntry[];
  groupId?: number;
  hideOnClose?: boolean;
  iconSize?: string;
  itemHeight?: string;
  last?: boolean;
  numberOfVisibleItems?: number;
  readonly?: boolean;
  transition?: boolean;
  toggle?: boolean;
  transitionDuration?: number;
};

export type BcMenuItemEmits = {
  (event: "click", payload: MouseEvent): void;
  (event: "toggle", payload: boolean): void;
};

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const slots = useSlots();
const currentInstance = getCurrentInstance();

const modelValue = defineModel<boolean>();

const {
  active = false,
  header = false,
  componentIndent = true,
  componentBackground = true, // set to false, when component has more child components, TODO: automatic detection
  indent = false,
  selected = false,
  beginGroup = false,
  borderAtTop = false,
  borderAtBottom = false,
  componentMaxHeight,
  componentPosition = "bottom",
  contextMenuActions = [],
  groupId,
  hideOnClose = true,
  iconSize = "1.9311em",
  itemHeight = "50px",
  last = false,
  numberOfVisibleItems,
  readonly = false,
  transition = true,
  toggle = true,
  transitionDuration = 500,
} = defineProps<BcMenuItemProps>();

const emit = defineEmits<BcMenuItemEmits>();

const rootRef = useTemplateRef("rootRef");
const contextMenuRef = useTemplateRef("contextMenuRef");

const isOpen = ref(false);

const showMenu = ref(false);
const menuX = ref(0);
const menuY = ref(0);

const children: ComponentInternalInstance[] = [];
provide("children", children);
const injectedChildren = inject<ComponentInternalInstance[]>("children", []);
const injectedBurgerMenuItems = inject<ComponentInternalInstance[]>(
  "burgerMenuItems",
  [],
);

const computedMaxHeight = computed(() => {
  if (componentMaxHeight) {
    return `calc(${componentMaxHeight})`;
  }

  if (numberOfVisibleItems) {
    return `calc(calc(${itemHeight}) * ${numberOfVisibleItems})`;
  }
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => {
  let style = (attrs.style ?? {}) as CSSProperties;
  style["--icon-size"] = iconSize;
  style["--item-level"] = itemLevel.value;
  return style;
});
const inheritedAttrs = computed(() => {
  const { class: _, style: __, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});
const itemLevel = computed(() => {
  let level = 0;
  if (rootRef.value) {
    let element = rootRef.value.parentElement;
    while (element !== null) {
      if (
        element.classList.contains("bissantzControls") &&
        element.classList.contains("burgerMenuControl")
      ) {
        break;
      }
      if (
        element.classList.contains("bissantzControls") &&
        element.classList.contains("menuItemControl")
      ) {
        level++;
      }
      element = element.parentElement;
    }
  }
  return level;
});

onMounted(() => {
  if (injectedChildren && currentInstance) {
    injectedChildren.push(currentInstance);
  }
  if (injectedBurgerMenuItems && currentInstance) {
    injectedBurgerMenuItems.push(currentInstance);
  }
  if (active) {
    isOpen.value = active;
    modelValue.value = active;
  }
});

function closeAllContextMenu(): void {
  if (injectedBurgerMenuItems) {
    injectedBurgerMenuItems.forEach((child) => {
      const menuItem = child.proxy as BcMenuItemInstance;
      if (
        menuItem &&
        menuItem.$.exposed &&
        toRaw(child) !== toRaw(currentInstance) &&
        menuItem.$.exposed.showMenu.value
      ) {
        menuItem.$.exposed.showMenu.value = false;
      }
    });
  }
}

function closeAllChildItems(): void {
  // Close all child items
  if (children) {
    children.forEach((child) => {
      const menuItem = child.proxy;
      if (
        menuItem &&
        menuItem.$.exposed &&
        menuItem.$.props &&
        menuItem.$slots.component &&
        menuItem.$.props.hideOnClose
      ) {
        menuItem.$.exposed.isOpen.value = false;
      }
    });
  }
}

function closeOtherItems(): void {
  if (injectedBurgerMenuItems) {
    injectedBurgerMenuItems.forEach((child) => {
      const menuItem = child.proxy;
      if (
        menuItem &&
        menuItem.$.exposed &&
        menuItem.$.props &&
        toRaw(child) !== toRaw(currentInstance) &&
        (typeof groupId !== "undefined" ? groupId !== menuItem.$.props.groupId : true) &&
        !getChildren(child).find((c) => toRaw(c) === toRaw(currentInstance)) &&
        menuItem.$slots.component
      ) {
        menuItem.$.exposed.isOpen.value = false;
      }
    });
  }
}

function getChildren(
  componentInstance: ComponentInternalInstance | null,
): ComponentInternalInstance[] {
  const componentChildren: ComponentInternalInstance[] = [];
  const proxy = componentInstance?.proxy;
  if (proxy && proxy.$.exposed && proxy.$.exposed.children) {
    (proxy.$.exposed.children as ComponentInternalInstance[]).forEach((child) => {
      collectChildren(child, componentChildren);
    });
  }
  return componentChildren;
}

function collectChildren(
  componentInstance: ComponentInternalInstance,
  componentChildren: ComponentInternalInstance[],
): void {
  const proxy = componentInstance.proxy;
  componentChildren.push(componentInstance);
  if (proxy && proxy.$.exposed && proxy.$.exposed.children) {
    (proxy.$.exposed.children as ComponentInternalInstance[]).forEach((child) => {
      collectChildren(child, componentChildren);
    });
  }
}

function onContextMenu(event: MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
  closeAllContextMenu();
  if (
    contextMenuActions.length &&
    (contextMenuRef.value
      ? !contextMenuRef.value.$el.contains(event.target as Node)
      : true)
  ) {
    showMenu.value = true;
    menuX.value = event.clientX;
    menuY.value = event.clientY;
  }
}

function onClick(payload: MouseEvent): void {
  if (toggle && !readonly && slots.component) {
    isOpen.value = !isOpen.value;
  }
  emit("click", payload);
}

watch(
  modelValue,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== isOpen.value) {
      isOpen.value = modelValue.value;
    }
  },
  { immediate: true },
);

watch(
  isOpen,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== isOpen.value) {
      modelValue.value = isOpen.value;
    }
    emit("toggle", isOpen.value);
    nextTick(() => {
      if (isOpen.value) {
        closeOtherItems();
      } else {
        closeAllChildItems();
      }
    });
  },
  { immediate: true },
);

defineExpose({
  attrs,
  modelValue,
  children,
  hideOnClose,
  isOpen,
  showMenu,
});
</script>

<template>
  <div
    v-on:contextmenu.prevent="onContextMenu($event)"
    class="bissantzControls menuItemControl"
    v-bind:class="[
      {
        beginGroup: beginGroup,
        borderAtTop: borderAtTop,
        borderAtBottom: borderAtBottom,
        last: last,
        opened: $slots.component && isOpen,
        readonly: readonly,
      },
      ...rootClass,
    ]"
    v-bind:style="rootStyle"
    ref="rootRef"
  >
    <template v-if="componentPosition === 'top'">
      <template v-if="!toggle">
        <div
          v-if="$slots.component"
          class="component"
          v-bind:class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          v-bind:style="{ maxHeight: computedMaxHeight }"
        >
          <slot name="component"></slot>
        </div>
      </template>
      <template v-else-if="transition">
        <BcCollapseTransition
          v-bind:duration="transitionDuration"
          v-bind="inheritedAttrs"
        >
          <div
            v-if="$slots.component"
            v-show="isOpen"
            class="component"
            v-bind:class="{
              componentIndent: componentIndent,
              componentBackground: componentBackground,
            }"
            v-bind:style="{ maxHeight: computedMaxHeight }"
          >
            <slot name="component"></slot>
          </div>
        </BcCollapseTransition>
      </template>
      <template v-else>
        <div
          v-if="$slots.component && isOpen"
          class="component"
          v-bind:class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          v-bind:style="{ maxHeight: computedMaxHeight }"
        >
          <slot name="component"></slot>
        </div>
      </template>
    </template>
    <BcButton
      class="menuTitle"
      plain
      fullWidth
      v-bind="inheritedAttrs"
      v-on:click="onClick($event)"
    >
      <slot v-if="$slots['title-icon']" name="title-icon"> </slot>
      <div
        class="titleText"
        v-bind:class="{
          header: header,
          indent: indent,
          selected: selected,
          noIcon: !$slots['title-icon'],
        }"
      >
        <slot name="title"></slot>
      </div>
    </BcButton>
    <template v-if="componentPosition === 'bottom'">
      <template v-if="!toggle">
        <div
          v-if="$slots.component"
          class="component"
          v-bind:class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          v-bind:style="{ maxHeight: computedMaxHeight }"
        >
          <slot name="component"></slot>
        </div>
      </template>
      <template v-else-if="transition">
        <BcCollapseTransition
          v-bind:duration="transitionDuration"
          v-bind="inheritedAttrs"
        >
          <div
            v-if="$slots.component"
            v-show="isOpen"
            class="component"
            v-bind:class="{
              componentIndent: componentIndent,
              componentBackground: componentBackground,
            }"
            v-bind:style="{ maxHeight: computedMaxHeight }"
          >
            <slot name="component"></slot>
          </div>
        </BcCollapseTransition>
      </template>
      <template v-else>
        <div
          v-if="$slots.component && isOpen"
          class="component"
          v-bind:class="{
            componentIndent: componentIndent,
            componentBackground: componentBackground,
          }"
          v-bind:style="{ maxHeight: computedMaxHeight }"
        >
          <slot name="component"></slot>
        </div>
      </template>
    </template>
    <BcContextMenu
      v-if="showMenu"
      v-bind:actions="contextMenuActions"
      v-bind:x="menuX"
      v-bind:y="menuY"
      v-bind:iconSize="iconSize"
      position="fixed"
      ref="contextMenuRef"
    />
  </div>
</template>

<style lang="less">
@import "../../styles/components/bc-menu-item.less";
</style>
